function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { forwardRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { TYPES } from '../../constants';
import { handleOnKeyPress } from '../../helper/handlers';
import ActionBar from '../common/ActionBar/ActionBar';
import InputWrapper from '../InputWrapper';
import styles from '@dtsl/css-input/dist/index.module.css';
import { Button } from '@dtsl/react/dist/components/Button';

// eslint-disable-next-line react/display-name
const TextArea = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    type = TYPES.TEXT,
    id = '',
    dataTestId = '',
    placeholder = '',
    disabled = false,
    required = false,
    readOnly = false,
    onKeyPress = () => {},
    maxCharCount = null,
    actions = [],
    actionsClass = '',
    actionsStyle = {},
    currCharCount = 0,
    primaryAction = null,
    style = {},
    isError = false,
    isSuccess = false,
    label = '',
    requiredLabel = '',
    helpText = '',
    helpButtonData = null,
    name = '',
    validationText = '',
    className = '',
    accessibleSuccessText = 'success',
    accessibleErrorText = 'error',
    ...restprops
  } = props;
  const textareaClasses = classNames({
    [styles['sib-input-textarea']]: true,
    [styles['sib-input-textarea--readonly']]: readOnly,
    [styles['sib-input-textarea--error']]: isError,
    [styles['sib-input-textarea--success']]: isSuccess
  });
  const ariaDescribedByIds = () => {
    const ids = [helpText && `${id}-helpText`, maxLength && `${id}-charCount`, validationText && (isError || isSuccess) && `${id}-validationText`].filter(Boolean);
    return ids.join(' ') || null;
  };
  const maxLength = !readOnly && !disabled ? maxCharCount : null;
  return /*#__PURE__*/React.createElement(InputWrapper, {
    label: label,
    id: id,
    required: required,
    requiredLabel: requiredLabel,
    isError: isError,
    isSuccess: isSuccess,
    helpText: helpText,
    helpButtonData: helpButtonData,
    validationText: validationText,
    style: style,
    className: className,
    accessibleErrorText: accessibleErrorText,
    accessibleSuccessText: accessibleSuccessText
  }, /*#__PURE__*/React.createElement("div", {
    className: textareaClasses,
    disabled: disabled
  }, /*#__PURE__*/React.createElement("textarea", _extends({}, restprops, {
    name: name,
    "data-testid": dataTestId ? dataTestId : null,
    ref: ref,
    type: type,
    className: styles['sib-input-common-control'],
    id: id,
    placeholder: placeholder ? placeholder : null,
    disabled: disabled,
    "aria-disabled": disabled ? disabled : null,
    required: required ? required : null,
    "aria-required": required ? true : null,
    "aria-invalid": isError,
    readOnly: readOnly ? true : null,
    "aria-readonly": readOnly ? readOnly : null,
    onKeyPress: event => handleOnKeyPress(event, onKeyPress, maxLength, event.target.value),
    maxLength: maxLength,
    "aria-labelledby": `${id}-label`,
    "aria-describedby": ariaDescribedByIds()
  })), /*#__PURE__*/React.createElement("div", {
    className: styles['sib-input-textarea__bottombar'],
    disabled: disabled
  }, /*#__PURE__*/React.createElement(ActionBar, {
    id: id,
    actions: actions,
    maxCharCount: maxLength,
    currCharCount: currCharCount,
    actionsClass: actionsClass,
    actionsStyle: actionsStyle,
    stretch: true,
    disableActions: disabled || readOnly
  }), primaryAction ? /*#__PURE__*/React.createElement(Button, _extends({}, primaryAction, {
    variant: "primary",
    size: "small",
    disabled: disabled || readOnly
  })) : null)));
});
TextArea.propTypes = {
  type: PropTypes.string,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  isError: PropTypes.bool,
  isSuccess: PropTypes.bool,
  onKeyPress: PropTypes.func,
  maxCharCount: PropTypes.number,
  actions: PropTypes.array,
  actionsClass: PropTypes.string,
  actionsStyle: PropTypes.object,
  currCharCount: PropTypes.number,
  primaryAction: PropTypes.object,
  style: PropTypes.object,
  helpText: PropTypes.string,
  helpButtonData: PropTypes.object,
  requiredLabel: (props, propName) => {
    const required = props['required'];
    const requiredLabel = props[propName];
    if (required && !requiredLabel) {
      return new Error(`Warning: 'requiredLabel' prop should be passed when 'required' prop is true.`);
    }
    if (typeof requiredLabel !== 'string') {
      return new Error(`Warning: 'requiredLabel should be a string.`);
    }
  },
  label: PropTypes.string,
  name: PropTypes.string,
  validationText: PropTypes.string,
  dataTestId: PropTypes.string,
  className: PropTypes.string,
  accessibleSuccessText: PropTypes.string,
  accessibleErrorText: PropTypes.string
};
export default TextArea;